import React from 'react'

import {
  Button, CrownFilled, Divider, FormElementWrapper, Icon, Radio,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { func, string } from 'prop-types'

import i18n from 'Pods/Shared/I18n'

import {
  TIER_ALL, TIER_ESSENTIALS, TIER_PLAN_NAMES, TIER_PREMIUM,
} from '../subscriptionTierFilters'

// Same tier colour language as the My Models chip (SubscriptionStatusTag): teal = Essentials,
// extra-purple + crown = Premium. A custom size object skips Triangle's .cgt-icon--<size>
// wrapper class, so the svg is 16px without a CSS override.
const CROWN_SIZE = { width: '16', height: '16' }

const premiumCrown = (
  <Icon
    svgComponent={CrownFilled}
    className="subscription-tier__crown"
    color="extra-purple"
    size={CROWN_SIZE}
    notSpaced
  />
)

// `name` scopes the native radio group: the desktop dropdown and the mobile All-filters panel
// can both be mounted, and a shared name would make the browser treat them as one group.
// `selectedTier` is nullable - no tier is checked until the search really is a subscription one.
// The trailing divider only renders when a class is given, since the desktop dropdown ends here.
function SubscriptionTier({
  selectedTier = null,
  subscriberTier = null,
  onChange,
  onClear,
  name,
  dividerClassName = null,
  locale = 'en',
}) {
  const sectionLabel = i18n.t('listingPage.filters.subscriptionTier.label', { ns: 'rails', lng: locale })
  const allLabel = i18n.t('listingPage.filters.subscriptionTier.all', { ns: 'rails', lng: locale })
  const yourPlanLabel = i18n.t('listingPage.filters.subscriptionTier.yourPlan', { ns: 'rails', lng: locale })
  const resetLabel = i18n.t('listingPage.filters.resetFilters', { ns: 'rails', lng: locale })

  function renderPlanLabel(tier) {
    return (
      <span className="subscription-tier__option-label">
        {TIER_PLAN_NAMES[tier]}
        {tier === TIER_PREMIUM && premiumCrown}
        {tier === subscriberTier && (
          <span className="subscription-tier__your-plan cgt-caption">{yourPlanLabel}</span>
        )}
      </span>
    )
  }

  const options = [
    { value: TIER_ALL, label: allLabel },
    { value: TIER_ESSENTIALS, label: renderPlanLabel(TIER_ESSENTIALS) },
    { value: TIER_PREMIUM, label: renderPlanLabel(TIER_PREMIUM) },
  ]

  return (
    <div className="subscription-tier">
      <FormElementWrapper title={sectionLabel}>
        <div role="radiogroup" aria-label={sectionLabel}>
          {options.map(({ value, label }) => (
            <Radio
              key={value}
              id={`${name}-${value}`}
              name={name}
              label={label}
              className="cgt-menu-item subscription-tier__radio"
              isChecked={selectedTier === value}
              onChange={() => onChange(value)}
            />
          ))}
        </div>
        {selectedTier && (
          <Button
            type="ghost"
            size="small"
            actionType="button"
            customClass="subscription-tier__reset"
            onClick={onClear}
          >
            {resetLabel}
          </Button>
        )}
      </FormElementWrapper>
      {dividerClassName && <Divider customClass={dividerClassName} />}
    </div>
  )
}

SubscriptionTier.propTypes = {
  selectedTier: string,
  subscriberTier: string,
  onChange: func.isRequired,
  onClear: func.isRequired,
  name: string.isRequired,
  dividerClassName: string,
  locale: string,
}

export default SubscriptionTier
